import dayjs from 'dayjs';
import styled from 'styled-components';

import { IconClose, IconSeeMore } from 'assets/icons';

import { UnfixedTicket, FixedTicket } from 'types/api';
import ProductStatusBadge from '../components/ProductStatusBadge';
import { useState } from 'react';
import { useMutationRequestBookingExcel } from '../graphQL/hooks/useMutationRequestBookingExcel';
import ExcelLoading from '../components/ExcelLoading';
import { useUserInformation } from 'graphQL/Zustand/useUserInformation';
import ExcelMobileAlert from '../components/ExcelMobileAlert';
import { SALE_LIST_PATH } from 'constants/path';
import { useNavigate } from 'react-router-dom';

const Header = styled.div<{ $isMobile: boolean }>`
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;

  z-index: 16;

  padding: 24px 20px;

  background-color: #fff;

  .HeaderWrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;

    margin-bottom: 4px;

    > h3 {
      margin: 0;
      margin-right: 8px;

      font-weight: 600;
      font-size: 24px;
      line-height: 32px;
      color: #1b1c1e;
      white-space: nowrap;

      @media (max-width: 768px) {
        font-size: 20px;
        line-height: 28px;
      }
    }
  }

  .badge-box {
    display: flex;
    flex-direction: row;
    gap: ${({ $isMobile }) => ($isMobile ? '4px' : '8px')};

    width: 100%;
  }

  .suffix-box {
    display: flex;
    align-items: center;
    gap: 24px;

    cursor: pointer;

    > svg {
      width: 32px;
      height: 32px;

      @media (max-width: 768px) {
        width: 24px;
        height: 24px;
      }
    }
  }
`;

const OptionBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  position: relative;

  > ul {
    display: flex;
    flex-direction: column;
    gap: 20px;

    position: absolute;
    top: 32px;
    right: 0;

    width: max-content;
    padding: 20px 16px;
    margin: 0px;

    border-radius: 12px;
    border: 0px;

    background-color: #fff;
    box-shadow: 0px 2px 10px 0px #00000026;

    li {
      list-style: none;

      font-weight: 500;
      font-size: 16px;
      line-height: 22px;

      color: #414b56;

      &.cancel {
        color: #ff4242;
      }
    }
  }
`;

const TimeTextBox = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  > p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;

    word-break: break-all;
  }

  > .date-text {
    margin-right: 4px;

    font-weight: 400;
    font-size: 14px;
    line-height: 20px;

    color: rgba(70, 71, 76, 1);
  }

  > .time-text {
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;

    color: rgba(70, 71, 76, 1);
  }
`;

interface Props {
  isMobile: boolean;
  startTime: number;
  endTime: number;
  item: FixedTicket | UnfixedTicket;
  handleModal: () => void;
  ticketType: 'fixed' | 'unfixed';
}

const AttendanceCheckModalHeader = ({
  isMobile,
  startTime,
  endTime,
  item,
  handleModal,
  ticketType,
}: Props) => {
  const navigate = useNavigate();

  const [isOptionBoxOpen, setIsOptionBoxOpen] = useState(false);
  const [isMobileExcelAlertOpen, setIsMobileExcelAlertOpen] = useState(false);

  const hostId = useUserInformation(state => state.userInfo.me.host.id);

  const { excelDownloadLoading, handleRequestBookingExcel } =
    useMutationRequestBookingExcel();

  const isSameDay =
    dayjs(startTime).format('YYYY-MM-DD') ===
    dayjs(endTime).format('YYYY-MM-DD');

  const isExactlySameDay = startTime === endTime;

  const getTimeDiff = () => {
    if (isSameDay) {
      const totalMinutes = dayjs(endTime).diff(dayjs(startTime), 'minute');
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      if (minutes === 0) {
        return `(${hours}시간)`;
      }

      return `(${hours}시간 ${minutes}분)`;
    }

    return `(${dayjs(endTime).diff(dayjs(startTime), 'day') + 1}일)`;
  };

  /* 옵션 박스 열기 */
  const handleModalOptionBox = () => {
    setIsOptionBoxOpen(prev => !prev);
  };

  const handleMobileExcelDownload = () => {
    setIsMobileExcelAlertOpen(prev => !prev);
  };

  /* 엑셀로 내려받기 */
  const handleGetBookingExcel = () => {
    if (isMobile) {
      handleMobileExcelDownload();
      return;
    }

    const fixedFilter = {
      scheduleId: item.id,
    };
    const unfixedFilter = {
      productIds: [
        (item as UnfixedTicket)?.bookingItem?.booking?.info?.product?.id || '',
      ],
      // unscheduled: true,
    };

    handleRequestBookingExcel({
      hostId,
      termTypes: ['STARTING'],
      term: {
        startedAt: startTime,
        endedAt: startTime,
      },
      ...(ticketType === 'fixed' ? fixedFilter : unfixedFilter),
    });
  };

  /* 판매내역에서 보기 */
  const goToSalesPage = () => {
    localStorage.setItem('scheduleIds', item.id);

    handleModalOptionBox();

    return navigate(SALE_LIST_PATH);
  };

  return (
    <>
      <Header $isMobile={isMobile}>
        <div className="HeaderWrapper">
          <h3>출석확인</h3>
          <div className="badge-box">
            <ProductStatusBadge item={item} hideCount />
          </div>

          <div className="suffix-box">
            <OptionBox onClick={handleModalOptionBox}>
              <IconSeeMore
                width={32}
                height={32}
                color={'rgba(112, 115, 124, 1)'}
              />

              {isOptionBoxOpen && (
                <ul>
                  {ticketType === 'fixed' && (
                    <li onClick={goToSalesPage}>판매내역에서 보기</li>
                  )}
                  <li onClick={handleGetBookingExcel}>엑셀로 내려받기</li>
                </ul>
              )}
            </OptionBox>

            <IconClose onClick={handleModal} color={'rgba(112, 115, 124, 1)'} />
          </div>
        </div>

        <TimeTextBox>
          <p className="date-text">
            {dayjs(startTime).format('M월 DD일(ddd)')}
          </p>
          <p className="time-text">{dayjs(startTime).format('HH:mm')} ~ </p>
          &nbsp;
          {isExactlySameDay ? (
            ''
          ) : (
            <>
              {!isSameDay && (
                <p className="date-text">
                  {dayjs(endTime).format('M월 DD일(ddd)')}
                </p>
              )}
              <p className="time-text">{dayjs(endTime).format('HH:mm')}</p>
              &nbsp;
              <p className="time-text">{getTimeDiff()}</p>
            </>
          )}
        </TimeTextBox>
      </Header>

      {excelDownloadLoading && <ExcelLoading />}
      {isMobile && isMobileExcelAlertOpen && (
        <ExcelMobileAlert handleModal={handleMobileExcelDownload} />
      )}
    </>
  );
};

export default AttendanceCheckModalHeader;
